import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { AudioTroubleshootingDialogComponent } from '../../components/dialogs/audio-troubleshooting/audio-troubleshooting-dialog.component';
import { DevicePermissionDialogComponent } from '../../components/dialogs/device-permission/device-permission-dialog.component';
import { LoadingState } from '../../components/ui/content-display-card/content-display-card.component';
import { ToastMessage, ToastMessageWithHeader } from './toast.dto';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastSubject = new Subject<ToastMessage>();
  toasts$ = this.toastSubject.asObservable();

  private loadingIndicatorSubject = new Subject<LoadingState>();
  loadingIndicator$ = this.loadingIndicatorSubject.asObservable();

  constructor(
    public snackbar: MatSnackBar,
    private router: Router,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  showAudioTroubleshootingDialog(): void {
    this.dialog.open(AudioTroubleshootingDialogComponent, {
      width: '90vw',
      maxWidth: '800px',
    });
  }

  /**
   * Shows a dialog for device permission errors with clear instructions
   * on how to allow access to camera and microphone.
   */
  showDevicePermissionError(): void {
    this.dialog.open(DevicePermissionDialogComponent, {
      width: '90vw',
      maxWidth: '800px',
    });
  }

  showWithHeader(toastMessage: ToastMessageWithHeader): void {
    this.toastSubject.next({ ...toastMessage, type: 'withHeader' });
  }

  showWithoutHeader(toastMessage: ToastMessage): void {
    this.toastSubject.next({ ...toastMessage, type: 'noHeader' });
  }

  /**
   * Displays a message in the loading indicator area.
   * Automatically clears success and error messages after a specified timeout.
   * @param state The loading state to display.
   * @param timeout The duration in milliseconds before success/error messages are cleared. Defaults to 3000ms.
   */
  showLoadingMessage(state: LoadingState, timeout: number = 3000): void {
    this.loadingIndicatorSubject.next(state);

    if (state.status === 'success' || state.status === 'error') {
      setTimeout(() => this.clearLoadingIndicator(), timeout);
    }
  }

  /**
   * Clears any message currently displayed in the loading indicator.
   */
  clearLoadingIndicator(): void {
    this.loadingIndicatorSubject.next({ status: 'idle' });
  }
}
